import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import OrderSummary from '../components/OrderSummary';
import BillingDetailsForm from '../components/BillingDetailsForm';
import PaymentMethods from '../components/PaymentMethods';
import { CheckCircle, XCircle, Mail, Pencil } from 'lucide-react';
import useOrderStatusPoll from '../hooks/useOrderStatusPoll';
import { downloadOrderInvoice } from '../utils/downloadOrderInvoice';
import { saveOrderInvoiceBilling } from '../utils/orderInvoiceBilling';
import { resolveCheckoutBillingInitialValues } from '../utils/resolveCheckoutBillingInitialValues';
import {
  buildLoginRedirectForCheckout,
  consumePendingInviteOrderId,
} from '../utils/invitePaymentFlow';
import { clearCheckoutCartBackup } from '../utils/cartCourseSelection';
import { buildCheckoutAuthHeaders, clearCheckoutAuthToken } from '../utils/checkoutSession';
import { API_URL } from '../config';
import { getMessage } from '../data/locales';
import { COURSE_ALREADY_OWNED_CODE } from '../utils/ownedCourseIds';
import { clearRegistrationFormDraft } from '../utils/registrationFormDraft';
import {
  buildCheckEmailFromCheckout,
  resolvePaidOrderAccess,
} from '../utils/checkoutEmailVerification';
import './CheckoutPage.css';

const EMPTY_BILLING_DEFAULTS = {};

const PAID_POLL_SYNC_MAX_ATTEMPTS = 3;
const PAID_POLL_SYNC_BACKOFF_MS = 500;
const CHECKOUT_STEP_PAYMENT = 'payment';

/**
 * Stores billing on the order. Failures are ignored so checkout can continue.
 * @param {string | null | undefined} orderId
 * @param {import('../utils/orderInvoiceBilling').OrderInvoiceBilling} details
 */
function persistOrderInvoiceBilling(orderId, details) {
  if (!orderId || !details) return;

  void fetch(`${API_URL}/api/pedidos/${orderId}/facturacion`, {
    method: 'PATCH',
    headers: buildCheckoutAuthHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify(details),
  }).catch(() => {
    // Session storage already has the billing data for this checkout.
  });
}

/**
 * @param {number} ms
 * @param {AbortSignal | undefined} signal
 * @returns {Promise<void>}
 */
function waitWithAbort(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timeoutId);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

const CheckoutPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('orderId');
  const checkoutStepParam = searchParams.get('step');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [billingDefaults, setBillingDefaults] = useState(null);
  const [billingDefaultsLoading, setBillingDefaultsLoading] = useState(true);
  const [step, setStep] = useState(1); // 1: Billing, 2: Payment
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [pollingOrderId, setPollingOrderId] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [retryingAfterFailure, setRetryingAfterFailure] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const isDownloadingRef = useRef(false);
  const paidPollRefreshRef = useRef(false);

  const { status: pollStatus, unlocked, error: pollError, isPolling, stop } = useOrderStatusPoll(pollingOrderId);

  const setCheckoutStepParam = useCallback((nextStep, options = {}) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextStep === CHECKOUT_STEP_PAYMENT) {
      nextParams.set('step', CHECKOUT_STEP_PAYMENT);
    } else {
      nextParams.delete('step');
    }
    setSearchParams(nextParams, options);
  }, [searchParams, setSearchParams]);

  const fetchOrder = React.useCallback(async (signal, options = {}) => {
    const { silent = false } = options;
    if (!orderId) return null;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await fetch(`${API_URL}/api/pedidos/${orderId}/status`, {
        headers: buildCheckoutAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        signal,
      });
      if (!res.ok) {
        if (res.status === 401) {
          clearCheckoutAuthToken();
          if (!silent) {
            navigate(buildLoginRedirectForCheckout(orderId), { replace: true });
          }
          return null;
        }
        if (res.status === 403) {
          clearCheckoutAuthToken();
          let forbiddenMessage = 'No tienes permiso para acceder a este pedido. Inicia sesión con la cuenta del alumno invitado.';
          try {
            const payload = await res.json();
            if (typeof payload?.error === 'string' && payload.error.trim()) {
              forbiddenMessage = payload.error.trim();
            }
          } catch {
            /* ignore malformed error body */
          }
          if (!silent) {
            setError(forbiddenMessage);
          }
          return null;
        }
        throw new Error('Failed to fetch order');
      }
      const data = await res.json();
      setOrder(data);
      if (data.status === 'paid' && data.unlocked) {
        setPollingOrderId(orderId);
      }
      return data;
    } catch (err) {
      if (err.name === 'AbortError') return null;
      console.error(err);
      if (!silent) {
        setError("Error cargando el pedido.");
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [orderId, navigate]);

  useEffect(() => {
    if (!orderId) {
      const recoveredOrderId = consumePendingInviteOrderId();
      if (recoveredOrderId) {
        navigate(`/checkout?orderId=${encodeURIComponent(recoveredOrderId)}`, { replace: true });
        return undefined;
      }
      setError("No se ha proporcionado un ID de pedido.");
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setOrder(null);
    setError(null);
    fetchOrder(controller.signal);
    return () => controller.abort();
  }, [orderId, retryKey, fetchOrder, navigate]);

  useEffect(() => {
    setBillingDetails(null);
    setStep(1);
    setBillingDefaults(null);
    setPollingOrderId(null);
    setIsProcessingPayment(false);
    setRetryingAfterFailure(false);

    if (!orderId) {
      setBillingDefaultsLoading(false);
      return;
    }

    let cancelled = false;

    const loadBillingDefaults = async () => {
      setBillingDefaultsLoading(true);
      try {
        const defaults = await resolveCheckoutBillingInitialValues(orderId);
        if (!cancelled) {
          setBillingDefaults(defaults);
        }
      } catch (err) {
        console.error('[CheckoutPage] Failed to load billing defaults:', err);
        if (!cancelled) {
          setBillingDefaults(EMPTY_BILLING_DEFAULTS);
        }
      } finally {
        if (!cancelled) {
          setBillingDefaultsLoading(false);
        }
      }
    };

    loadBillingDefaults();

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  useEffect(() => {
    const requestedPaymentStep = checkoutStepParam === CHECKOUT_STEP_PAYMENT;
    setStep(requestedPaymentStep && billingDetails ? 2 : 1);
  }, [billingDetails, checkoutStepParam]);

  useEffect(() => {
    const paid = (order?.status === "paid" && order?.unlocked === true) || pollStatus === "paid";
    if (paid) {
      clearRegistrationFormDraft();
    }
  }, [order, pollStatus]);

  // Tras "Intentar de nuevo" (retryingAfterFailure), no limpiar el modo reintento en los handlers de pago:
  // si order.status sigue en 'failed' en estado React, isFailed volvería a ganar y mostraría la pantalla de error.
  // Solo quitamos retry cuando el backend / el poll confirman que ya no estamos en fallo.
  useEffect(() => {
    if (!retryingAfterFailure) return;
    if (order && order.status !== 'failed') {
      setRetryingAfterFailure(false);
    }
  }, [retryingAfterFailure, order, order?.status]);

  useEffect(() => {
    if (!retryingAfterFailure) return;
    if (!pollingOrderId || String(pollingOrderId) !== String(orderId)) return;
    if (pollError || pollStatus === 'failed') return;
    // Poll activo: el pedido pasó a flujo de pago en curso o completado (p. ej. tras handleRetry + nuevo intento)
    if (pollStatus === 'processing' || pollStatus === 'paid') {
      setRetryingAfterFailure(false);
    }
  }, [retryingAfterFailure, pollingOrderId, orderId, pollStatus, pollError]);

  useEffect(() => {
    paidPollRefreshRef.current = false;
  }, [pollingOrderId]);

  useEffect(() => {
    const paymentCompleted =
      (order?.status === 'paid' && order?.unlocked === true) ||
      (pollStatus === 'paid' && unlocked === true);
    if (paymentCompleted) {
      localStorage.removeItem('carrito');
      clearCheckoutCartBackup();
      clearCheckoutAuthToken();
    }
  }, [order?.status, order?.unlocked, pollStatus, unlocked]);

  useEffect(() => {
    if (pollStatus !== 'paid' || !unlocked || paidPollRefreshRef.current) return;
    if (order?.status === 'paid' && order?.unlocked === true) return;

    let cancelled = false;
    const controller = new AbortController();

    const markPaidPollSyncComplete = () => {
      paidPollRefreshRef.current = true;
      setOrder((prev) => {
        if (!prev || (prev.status === 'paid' && prev.unlocked === true)) return prev;
        return { ...prev, status: 'paid', unlocked: true };
      });
    };

    void (async () => {
      try {
        for (let attempt = 0; attempt < PAID_POLL_SYNC_MAX_ATTEMPTS; attempt += 1) {
          if (cancelled) return;

          if (attempt > 0) {
            await waitWithAbort(PAID_POLL_SYNC_BACKOFF_MS * attempt, controller.signal);
          }

          const data = await fetchOrder(controller.signal, { silent: true });
          if (cancelled) return;

          if (data?.status === 'paid' && data?.unlocked === true) {
            paidPollRefreshRef.current = true;
            return;
          }
        }

        if (!cancelled) {
          markPaidPollSyncComplete();
        }
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        markPaidPollSyncComplete();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [pollStatus, unlocked, order?.status, order?.unlocked, fetchOrder]);

  // Calculate installment options
  const billingInitialValues = useMemo(
    () => billingDetails || billingDefaults || EMPTY_BILLING_DEFAULTS,
    [billingDetails, billingDefaults],
  );

  const installmentOptions = useMemo(() => {
      if (!order || !order.items) return [];
      
      const allAllow = order.items.every(item => item.allowInstallments);
      if (!allAllow) return [];

      const optionsSets = order.items.map(item => item.installmentsAllowed).filter(s => s && s.length > 0);
      if (optionsSets.length === 0) return [];

      // Intersection
      const intersection = optionsSets.reduce((a, b) => a.filter(c => b.includes(c)));
      return intersection;
  }, [order]);


  const handleBillingSubmit = (details) => {
      setBillingDetails(details);
      saveOrderInvoiceBilling(orderId, details);
      persistOrderInvoiceBilling(orderId, details);
      setStep(2);
      setCheckoutStepParam(CHECKOUT_STEP_PAYMENT);
  };

  const handleDownloadInvoice = async () => {
      if (isDownloadingRef.current) return;
      isDownloadingRef.current = true;
      setIsDownloading(true);
      setInvoiceError(null);
      try {
          await downloadOrderInvoice({ order, orderId, billing: billingDetails });
      } catch (err) {
          const message = err instanceof Error ? err.message : 'No se pudo generar la factura.';
          setInvoiceError(message);
      } finally {
          isDownloadingRef.current = false;
          setIsDownloading(false);
      }
  };

  const handlePaymentInitiated = useCallback(() => {
    setIsProcessingPayment(true);
    setPollingOrderId(orderId);
  }, [orderId]);

  const handlePaymentError = useCallback((msg) => {
    setIsProcessingPayment(false);
    setError(typeof msg === 'string' ? msg : msg?.message || 'Error de pago');
  }, []);

  const handleStartProcessing = useCallback(() => {
    setIsProcessingPayment(true);
    setError(null);
  }, []);

  /** Salir del modo reintento solo cuando el pedido ya no está en failed (evita volver a la tarjeta de error por estado obsoleto). */
  useEffect(() => {
    if (!retryingAfterFailure || !order) return;
    if (order.status !== 'failed') {
      setRetryingAfterFailure(false);
    }
  }, [retryingAfterFailure, order]);

  const handleRetryFetch = () => {
    setError(null);
    setLoading(true);
    setRetryKey((k) => k + 1);
  };

  const handleReturnToCheckout = () => {
    setError(null);
    setLoading(true);
    if (orderId) {
      navigate(`/checkout?orderId=${encodeURIComponent(orderId)}`, { replace: true });
    } else {
      navigate('/checkout', { replace: true });
    }
    // Navigating to the current canonical URL is a no-op in React Router.
    // Force a fresh order request so the error screen can actually be left.
    setRetryKey((key) => key + 1);
  };

  const handleRetry = () => {
      setRetryingAfterFailure(true);
      stop();
      setIsProcessingPayment(false);
      setPollingOrderId(null);
      setError(null);
      fetchOrder(undefined);
  };

  if (loading) {
    return (
      <div className="container checkout-page">
        <div className="d-flex justify-content-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  const errorMessage = typeof error === 'string' ? error : (error?.message || '');
  const isEmailNotVerified = /verific(a|ar).*(correo|email)/i.test(errorMessage);
  if (error) {
    return (
      <div className="container checkout-page">
        <div className="card shadow-lg border-0 p-5 mx-auto" style={{ maxWidth: '600px' }}>
          <div className={`mb-4 text-center ${isEmailNotVerified ? 'text-warning' : 'text-danger'}`}>
            {isEmailNotVerified ? <Mail size={64} className="mx-auto" /> : <XCircle size={64} className="mx-auto" />}
          </div>
          <h2 className="mb-3 text-center">{isEmailNotVerified ? 'Verifica tu correo' : 'Ha ocurrido un error'}</h2>
          <p className="text-muted mb-4 text-center">{errorMessage}</p>
          {isEmailNotVerified ? (
            <div className="d-grid gap-2">
              <Link to="/check-email" className="btn btn-primary btn-lg">
                Revisar correo de verificación
              </Link>
              <button type="button" className="btn btn-outline-secondary" onClick={handleReturnToCheckout}>
                 Volver al checkout
               </button>
            </div>
          ) : (
            <div className="text-center">
              <button type="button" className="btn btn-primary" onClick={handleRetryFetch}>
                Volver a intentar
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container checkout-page">
        <div className="card shadow-lg border-0 p-5 mx-auto" style={{ maxWidth: '600px' }}>
          <div className="mb-4 text-warning text-center">
            <XCircle size={64} className="mx-auto" />
          </div>
          <h2 className="mb-3 text-center">Pedido no encontrado</h2>
          <p className="text-muted mb-4 text-center">No se ha encontrado el pedido. Comprueba el enlace o intenta de nuevo.</p>
          <div className="text-center">
            <Link to="/cursos" className="btn btn-primary">Ir a cursos</Link>
          </div>
        </div>
      </div>
    );
  }

  const isPurchaseBlocked =
    order?.blockedPurchase?.code === COURSE_ALREADY_OWNED_CODE && order.status !== "paid";

  if (isPurchaseBlocked) {
    return (
      <div className="container checkout-page">
        <div className="card shadow-lg border-0 p-5 mx-auto" style={{ maxWidth: "600px" }}>
          <div className="mb-4 text-center text-warning">
            <XCircle size={64} className="mx-auto" />
          </div>
          <h2 className="mb-3 text-center">{getMessage("checkout.alreadyOwnedButton")}</h2>
          <p className="text-muted mb-4 text-center">
            {order.blockedPurchase.error || getMessage("checkout.alreadyOwnedOrderBlocked")}
          </p>
          <div className="text-center">
            <Link to="/usuario/campus" className="btn btn-primary">
              {getMessage("checkout.goToCampus")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Determine if we are in success state.
  // accessMode comes from the order status. Missing values keep the previous screen.
  const paidAccess = resolvePaidOrderAccess(order);
  const isSuccess = (order?.status === 'paid' && order?.unlocked === true) || (pollStatus === 'paid' && unlocked);
  const hasActivePollFailure = Boolean(pollingOrderId) && (pollStatus === 'failed' || Boolean(pollError));
  const isFailed = (order && order.status === 'failed') || hasActivePollFailure;

  if (isSuccess) {
    const purchasedCourses = (order?.items || [])
      .map((item) => ({
        id: item?.cursoId,
        title: typeof item?.title === 'string' ? item.title.trim() : '',
      }))
      .filter((item) => item.title);
    const purchasedCoursePath =
      purchasedCourses.length === 1 && purchasedCourses[0].id
        ? `/usuario/curso/${purchasedCourses[0].id}`
        : '/usuario/campus';
    return (
        <div className="container checkout-page text-center">
            <div className="card shadow-lg border-0 p-5 mx-auto" style={{ maxWidth: '600px' }}>
                <div className="mb-4 text-success">
                    <CheckCircle size={64} className="mx-auto" />
                </div>
                <h2 className="mb-3">¡Pago completado con éxito!</h2>
                <p className="lead text-muted mb-4">
                    Tu pedido <strong>#{orderId.slice(-6)}</strong> ha sido procesado correctamente.
                </p>
                {purchasedCourses.length > 0 ? (
                  <section className="checkout-success-courses" aria-labelledby="checkout-success-courses-title">
                    <h3 id="checkout-success-courses-title">
                      {purchasedCourses.length === 1 ? 'Curso adquirido' : 'Cursos adquiridos'}
                    </h3>
                    <ul>
                      {purchasedCourses.map((course, index) => (
                        <li key={course.id || `${course.title}-${index}`}>{course.title}</li>
                      ))}
                    </ul>
                  </section>
                ) : null}
                <div className="d-grid gap-3 col-md-8 mx-auto">
                    {paidAccess === 'code' ? (
                      <>
                        <p className="mb-0">{getMessage("checkout.successVerifyIntro")}</p>
                        <Link
                          to={buildCheckEmailFromCheckout(order, orderId)}
                          className="btn btn-primary btn-lg"
                        >
                          {getMessage("checkout.successVerifyCta")}
                        </Link>
                      </>
                    ) : null}
                    {paidAccess === 'login' ? (
                      <>
                        <p className="mb-0">{getMessage("checkout.successExistingAccount")}</p>
                        <Link to="/login" className="btn btn-primary btn-lg">
                          {getMessage("checkout.successExistingAccountCta")}
                        </Link>
                      </>
                    ) : null}
                    {paidAccess === 'course' ? (
                    <Link to={purchasedCoursePath} className="btn btn-primary btn-lg">
                        {purchasedCourses.length === 1
                          ? `Acceder a ${purchasedCourses[0].title}`
                          : 'Ir a mis cursos'}
                    </Link>
                    ) : null}
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleDownloadInvoice}
                        disabled={isDownloading || order?.status !== 'paid'}
                        aria-busy={isDownloading}
                    >
                        Descargar Factura
                    </button>
                    {invoiceError && (
                        <p className="text-danger small mb-0" role="alert">{invoiceError}</p>
                    )}
                </div>
            </div>
        </div>
      );
  }

  if (isFailed && !retryingAfterFailure) {
      return (
        <div className="container checkout-page text-center">
            <div className="card shadow-lg border-0 p-5 mx-auto" style={{ maxWidth: '600px' }}>
                <div className="mb-4 text-danger">
                    <XCircle size={64} className="mx-auto" />
                </div>
                <h2 className="mb-3">Ha ocurrido un error</h2>
                <p className="text-muted mb-4">
                    {pollError || "No se ha podido confirmar el pago."}
                </p>
                <button onClick={handleRetry} className="btn btn-primary">
                    Intentar de nuevo
                </button>
            </div>
        </div>
      );
  }

  return (
    <div className="container checkout-page">
        <h2 className="mb-4 checkout-page__title">Finalizar Compra</h2>
        
        <div className="row">
            <div className="col-md-8">
                {step === 1 && (
                      <BillingDetailsForm
                        onSubmit={handleBillingSubmit}
                        initialValues={billingInitialValues}
                        defaultsLoading={billingDefaultsLoading}
                      />
                )}

                {(step === 2 || retryingAfterFailure) && (
                    <div className="mb-4 payment-method-mobile-margin">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4>Método de Pago</h4>
                            {!retryingAfterFailure && (
                              <button
                                type="button"
                                className="btn btn-sm checkout-page__edit-billing-btn"
                                onClick={() => {
                                  setStep(1);
                                  setCheckoutStepParam('billing', { replace: true });
                                }}
                                disabled={isProcessingPayment || isPolling}
                              >
                                <Pencil size={14} aria-hidden="true" />
                                Volver al checkout
                              </button>
                            )}
                        </div>
                        
                        {(isProcessingPayment || isPolling) ? (
                            <div className="card p-5 text-center">
                                <div className="mb-3">
                                    <div className="spinner-border text-primary" role="status"></div>
                                </div>
                                <h5>Procesando tu pago...</h5>
                                <p className="text-muted">Pago recibido — esperando confirmación segura...</p>
                                <p className="small text-muted">No cierres esta ventana.</p>
                            </div>
                        ) : (
                            <PaymentMethods 
                                orderId={orderId}
                                userEmail={billingDetails?.email}
                                billingDetails={billingDetails}
                                installmentOptions={installmentOptions}
                                onPaymentSuccess={handlePaymentInitiated}
                                onPaymentError={handlePaymentError}
                                onStartProcessing={handleStartProcessing}
                            />
                        )}
                    </div>
                )}
            </div>

            <div className="col-md-4">
                <OrderSummary 
                    items={order.items || []} 
                    total={order.amount} 
                    currency={order.currency}
                />
                
                <div className="card mt-3 bg-light border-0">
                    <div className="card-body">
                        <small className="text-muted">
                            <CheckCircle size={14} className="me-1 d-inline" />
                            Pago 100% Seguro. Tus datos están protegidos.
                        </small>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default CheckoutPage;
